const getSpotlightTargetElement = (target, { useOuterTarget = false } = {}) => {
  if (!target || useOuterTarget) return target

  const interactiveChild = target.querySelector('button, a, [role="button"]')
  return interactiveChild || target
}

const getElementsToObserve = (target, options = {}) => {
  const elements = [target]
  const inner = getSpotlightTargetElement(target, options)
  if (inner && inner !== target) {
    elements.push(inner)
  }
  return elements
}

/**
 * Align react-joyride spotlight with the target element's live layout box.
 * Reads dimensions from the interactive child when present (e.g. icon vs full ILP button).
 */
export const adjustEducationJoyrideSpotlight = (
  targetSelector,
  { padding = 4, useOuterTarget = false } = {}
) => {
  const spotlight = document.querySelector('.react-joyride__spotlight')
  const target = document.querySelector(targetSelector)
  if (!spotlight || !target) return

  const spotlightTarget = getSpotlightTargetElement(target, { useOuterTarget })
  const rect = spotlightTarget.getBoundingClientRect()
  const { borderRadius } = window.getComputedStyle(spotlightTarget)

  // Viewport-relative coords require fixed positioning (joyride defaults to absolute).
  spotlight.style.position = 'fixed'
  spotlight.style.width = `${Math.round(rect.width + padding * 2)}px`
  spotlight.style.height = `${Math.round(rect.height + padding * 2)}px`
  spotlight.style.left = `${Math.round(rect.left - padding)}px`
  spotlight.style.top = `${Math.round(rect.top - padding)}px`
  spotlight.style.borderRadius = borderRadius
}

export const repositionEducationJoyrideTooltip = () => {
  window.dispatchEvent(new Event('resize'))
}

/**
 * Keep spotlight aligned while a joyride is running, including when the target
 * is replaced or resized after async UI updates (e.g. ILP full button → icon).
 */
export const observeEducationJoyrideSpotlight = (
  targetSelector,
  { padding = 4, useOuterTarget = false } = {}
) => {
  let lastTop = null
  let lastLeft = null
  let menuObserver

  const adjustSpotlight = () => {
    const target = document.querySelector(targetSelector)
    adjustEducationJoyrideSpotlight(targetSelector, { padding, useOuterTarget })

    if (!target) return

    const { top, left } = target.getBoundingClientRect()
    const roundedTop = Math.round(top)
    const roundedLeft = Math.round(left)

    if (
      lastTop !== null &&
      (lastTop !== roundedTop || lastLeft !== roundedLeft)
    ) {
      repositionEducationJoyrideTooltip()
    }

    lastTop = roundedTop
    lastLeft = roundedLeft
  }

  const observedTargets = new Set()
  let resizeObserver
  let resizeTimer
  let scrollTimer
  let mutationTimer

  const unobserveAll = () => {
    observedTargets.forEach((element) => resizeObserver?.unobserve(element))
    observedTargets.clear()
  }

  const observeTargetElements = () => {
    const target = document.querySelector(targetSelector)
    if (!target) return

    getElementsToObserve(target, { useOuterTarget }).forEach((element) => {
      if (!observedTargets.has(element)) {
        resizeObserver.observe(element)
        observedTargets.add(element)
      }
    })
  }

  const refreshObservation = () => {
    unobserveAll()
    observeTargetElements()
    adjustSpotlight()
  }

  resizeObserver = new ResizeObserver(adjustSpotlight)

  const scheduleRefresh = () => {
    clearTimeout(mutationTimer)
    mutationTimer = setTimeout(refreshObservation, 50)
  }

  adjustSpotlight()
  observeTargetElements()

  const menu = document.querySelector(targetSelector)?.closest('.ant-menu')
  if (menu) {
    menuObserver = new MutationObserver(scheduleRefresh)
    menuObserver.observe(menu, { childList: true, subtree: true })
  }

  const mutationObserver = new MutationObserver(scheduleRefresh)
  mutationObserver.observe(document.body, { childList: true, subtree: true })

  const handleWindowResize = () => {
    clearTimeout(resizeTimer)
    resizeTimer = setTimeout(refreshObservation, 250)
  }
  const handleScroll = () => {
    clearTimeout(scrollTimer)
    scrollTimer = setTimeout(adjustSpotlight, 16)
  }

  window.addEventListener('resize', handleWindowResize)
  window.addEventListener('scroll', handleScroll, true)

  return () => {
    clearTimeout(mutationTimer)
    clearTimeout(resizeTimer)
    clearTimeout(scrollTimer)
    resizeObserver.disconnect()
    mutationObserver.disconnect()
    menuObserver?.disconnect()
    window.removeEventListener('resize', handleWindowResize)
    window.removeEventListener('scroll', handleScroll, true)
  }
}
